import { LocalGridType, ProjectVersionFragment } from '../generated/graphql'
import {
  BuildingCalculator,
  createMapBuildingToBuildingCalculator,
} from './BuildingCalculator'
import {
  createMapLocalGridToLocalGridCalculator,
  LocalGridCalculator,
} from './LocalGridCalculator'
import { createProvided, EnergyType, Quadrant } from './EnergyFlow'
import { SystemContainerCalculator } from './SystemContainerCalculator'
import { groupBy, reduceToSumOfItems } from '../util/array-utility'
import { providerProperties } from '../provider/provider-properties'
import { localGridTypeToProviderTypeMap } from '../local-grid/local-grid-util'
import { SpecialDemand } from './special-demand-ids'
import {
  createMapEnergyBusToEnergyBusCalculator,
  EnergyBusCalculator,
} from './EnergyBusCalculator'

export class VersionCalculator {
  /** Individual, unique buildings without multipliers applied */
  private readonly buildings: BuildingCalculator[] = []

  private readonly localGrids: LocalGridCalculator[] = []

  private energyBus: EnergyBusCalculator | undefined

  private hasProvided = false

  public addBuilding(building: BuildingCalculator): VersionCalculator {
    this.buildings.push(building)

    return this
  }

  public addLocalGrid(localGrid: LocalGridCalculator): VersionCalculator {
    this.localGrids.push(localGrid)

    return this
  }

  public addEnergyBus(energyBus: EnergyBusCalculator): VersionCalculator {
    this.energyBus = energyBus

    return this
  }

  public calculateUsage(): void {
    this.getNonMultipliedBuildings().forEach((building) =>
      building.calculateUsage(),
    )

    const localWaterGrid = this.getLocalGrids().find(
      (localGrid) => localGrid.type === LocalGridType.Water,
    )
    const localElectricalGrid = this.getLocalGrids().find(
      (localGrid) => localGrid.type === LocalGridType.Electricity,
    )

    if (localWaterGrid) {
      this.transferDemands(
        this.getMultipliedBuildings(),
        localWaterGrid,
        SpecialDemand.Buildings,
      )
    }
    if (localElectricalGrid) {
      this.transferDemands(
        this.getMultipliedBuildings(),
        localElectricalGrid,
        SpecialDemand.Buildings,
      )
    }

    localWaterGrid?.calculateUsage()

    if (localWaterGrid && localElectricalGrid) {
      this.transferDemands(
        [localWaterGrid],
        localElectricalGrid,
        SpecialDemand.LocalWaterGrid,
      )
    }

    localElectricalGrid?.calculateUsage()

    this.energyBus?.calculateUsage()

    this.hasProvided = true
  }

  public checkBalance(): void {
    if (!this.hasProvided) {
      throw new Error('Please calculate usage before checking balance.')
    }

    this.buildings.forEach((building) => building.checkBalance())
    this.localGrids.forEach((localGrid) => localGrid.checkBalance())

    if (this.energyBus) {
      this.energyBus.checkBalance()
    }
  }

  /** Get individual buildings without using the multiplier */
  public getNonMultipliedBuildings(): BuildingCalculator[] {
    return this.buildings
  }

  /** Get all buildings using multipliers */
  public getMultipliedBuildings(): BuildingCalculator[] {
    return this.getNonMultipliedBuildings().flatMap((building) =>
      Array.from({ length: building.multiplier }, () => building),
    )
  }

  public getLocalGrids(): LocalGridCalculator[] {
    return this.localGrids
  }

  /** Get all local grid and building containers with the buildings multiplied */
  public getAllContainers(): SystemContainerCalculator[] {
    return [...this.getLocalGrids(), ...this.getMultipliedBuildings()]
  }

  public findBuilding(buildingId: string): BuildingCalculator | undefined {
    return this.buildings.find((building) => building.id === buildingId)
  }

  public findLocalGrid(localGridId: string): LocalGridCalculator | undefined {
    return this.localGrids.find((localGrid) => localGrid.id === localGridId)
  }

  public findEnergyBus(): EnergyBusCalculator | undefined {
    return this.energyBus
  }

  private transferDemands(
    from: SystemContainerCalculator[],
    to: LocalGridCalculator,
    addedDemandId: string,
  ): void {
    const providerType = localGridTypeToProviderTypeMap[to.type]
    const energyTypes = providerProperties[providerType].providesEnergyTypes

    const transferrableFlows = from
      .flatMap((calculator) => calculator.getProviders())
      .filter((provider) => provider.type === providerType)
      .flatMap((provider) => provider.flows ?? [])
      .filter(
        (flow) =>
          flow.quadrant === Quadrant.Requirement &&
          energyTypes.includes(flow.energyType),
      )

    const transferrableFlowsByEnergyType = groupBy(
      transferrableFlows,
      (flow) => flow.energyType,
    )

    const localGridDemands = Object.entries(transferrableFlowsByEnergyType).map(
      ([energyType, flows]) =>
        createProvided(
          addedDemandId,
          energyType as EnergyType,
          flows.map((flow) => flow.hourSeries).reduce(reduceToSumOfItems, []),
        ),
    )

    localGridDemands.forEach((flow) => to.addDemand(flow))
  }
}

export function mapVersionToVersionCalculator(
  version: ProjectVersionFragment,
  temperatureValues?: number[],
): VersionCalculator {
  const newVersionCalculator = new VersionCalculator()

  version.buildings
    .map(createMapBuildingToBuildingCalculator(temperatureValues))
    .forEach((buildingCalculator) =>
      newVersionCalculator.addBuilding(buildingCalculator),
    )

  version.localGrids
    .map(createMapLocalGridToLocalGridCalculator(temperatureValues))
    .forEach((localGridCalculator) =>
      newVersionCalculator.addLocalGrid(localGridCalculator),
    )

  if (version.energyBus) {
    const systemContainers = newVersionCalculator.getAllContainers()

    const mapEnergyBusToEnergyBusCalculator =
      createMapEnergyBusToEnergyBusCalculator(systemContainers)

    newVersionCalculator.addEnergyBus(
      mapEnergyBusToEnergyBusCalculator(version.energyBus),
    )
  }

  return newVersionCalculator
}
